import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Sentry from '@sentry/react';
import { Close, FileDownloadOutlined, DeleteOutline } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Grid2,
} from '@mui/material';

import {
  gray100,
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import {
  boldMd,
  boldXxl,
  dialogTitle as dialogTitleStyle,
  regularLg,
  regularMd,
  regularSm,
  regularXs,
  sectionTitle,
  sideBarWidth,
  spHeaderHeight,
  spHeaderTitle,
} from '../../../constants/location';
import { inputCsvUploadSchema, validateMasterCsvFile } from '../../../validation/input';
import { type InputUploadType } from '../../../types/input';
import { MasterMappingType, MasterType } from '../../../types/master';
import {
  batchStatus,
  masterPageTab,
  masterPageTabLabel,
  masterStatus,
  type MasterPageTab,
} from '../../../constants/enum';
import { resolveSearchKey } from '../../../utils/master/matchKeys';
import { createMaster } from '../../../api/firebase/firestore/master/create';
import { getMastersByCompanyId } from '../../../api/firebase/firestore/master/read';
import {
  updateMaster,
  updateMastersDisplayOrder,
} from '../../../api/firebase/firestore/master/update';
import { deleteMaster } from '../../../api/firebase/firestore/master/delete';
import { getMasterMappings } from '../../../api/firebase/firestore/master/mapping/read';
import { createMasterMapping } from '../../../api/firebase/firestore/master/mapping/create';
import { updateMasterMapping } from '../../../api/firebase/firestore/master/mapping/update';
import { deleteMasterMapping } from '../../../api/firebase/firestore/master/mapping/delete';
import { updateMasterMappingOrders } from '../../../api/firebase/firestore/master/mapping/updateOrders';
import {
  registerMasterDataset,
  renameMasterFieldMapping,
  getMasterCsvSignedUploadUrl,
  getMasterCsvSignedDownloadUrl,
  deleteMasterFieldMapping,
  syncMasterBatchStatus,
} from '../../../api/firebase/function/handleMasterVerification';

import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSelectField from '../../uiParts/form/CustomSelectField';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import FileUploader from '../../uiParts/form/FileUploader';
import SideBar from '../../uiParts/menu/SideBar';
import MasterMappingFormModal, {
  type MasterMappingFormResult,
} from '../../uiParts/modal/MasterMappingFormModal';
import PatternMappingTab, { type MappingStep, type PatternMappingGroup } from './PatternMappingTab';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import MetaTitle from '../../utils/MetaTitle';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import AccountCircle from '../../assets/Account circle.svg';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../../api/firebase/config';
import { usePatternList } from '../../../hooks/usePatternList';
import { buildPatternCreatedAtMap, sortMastersByPattern } from '../../../utils/masterSort';
import {
  buildMasterRecordLimitErrorMessage,
  checkMasterRecordLimit,
  sumMasterRowCount,
} from '../../../utils/masterRecordLimit';
import useSnackbar from '../../../hooks/useSnackbar';
import { getCsvRowCount } from '../../../utils/csvRowCount';
import { buildUploaderFields, getDisplayName } from '../../../utils/displayName';
import { FirebaseError } from '@firebase/util';
import { getUserErrorMessage } from '../../../modules/error';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import moment from 'moment';

const styles = {
  avatar: {
    height: '24px',
    width: '24px',
  },
  buttonSmall: {
    height: '36px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  // タブの右に置く「最新の状態に更新」ボタン（#3416）。タブ（高さ 24px）と並べるため小さくする。
  reloadButton: {
    ...regularXs,
    height: '24px',
    minWidth: 0,
    padding: '0 8px',
    whiteSpace: 'nowrap',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  headerButtonWrapper: {
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
  },
  uploadButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    padding: '16px',
    '& .MuiButton-icon': { marginRight: '4px' },
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitleText: {
    ...dialogTitleStyle,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  // パターン別設定タブ用ダイアログのボタン行（#3180）
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
  },
  dialogCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  dialogSubmitButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  icon18: {
    height: '18px',
    marginLeft: '0px',
    width: '18px',
  },
  iconButton: {
    padding: 0,
  },
  sidebar: {
    backgroundColor: gray100,
    height: '100vh',
    position: 'fixed',
    width: sideBarWidth,
    '@media screen and (max-width:960px)': {
      height: spHeaderHeight,
      width: '100%',
      zIndex: '100',
    },
  },
  table: {
    minWidth: '960px',
    overflowX: 'auto',
  },
  tableCellAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
  },
  tableCellButton: {
    padding: '0 16px 0 0',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableDataRow: {
    height: '48px',
  },
  // タブ（#3180）。選択中タブ・下線は黒(gray100)、非選択はグレー(gray80)。
  // 他画面のタブ（照合トレース・照合モーダル）と配色を揃え、MUI デフォルトの青は使わない。
  // 画面切り替えタブ（#3180）。
  // OCR結果一覧（未処理/確認済/出力済）と同じチップ型に揃える。
  tabWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: '12px',
    margin: '0 32px',
    '@media screen and (max-width:960px)': {
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
  tab: {
    alignItems: 'center',
    backgroundColor: gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    // ラベルの長さに合わせて伸ばす（#3308）。
    // 「パターンごとの照合設定」は固定幅120pxに収まらないため、
    // 幅は下限だけ決めて、左右の余白ぶん広がるようにする。
    minWidth: '120px',
    padding: '0 12px',
    whiteSpace: 'nowrap',
    '@media screen and (max-width:960px)': {
      // SP では 50% 固定だとラベルの長さが違う2つが不揃いに見えるため、
      // 内容に応じた比率で分け合う。
      flex: 1,
      minWidth: 0,
      height: 'auto',
      minHeight: '24px',
      padding: '2px 4px',
      boxSizing: 'border-box',
    },
  },
  tabActive: {
    backgroundColor: gray100,
  },
  tabText: {
    ...regularXs,
    color: gray100,
    '@media screen and (max-width:960px)': {
      fontSize: '11px',
      textAlign: 'center',
    },
  },
  tabTextActive: {
    color: white,
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  emptyIcon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  emptyTitle: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    ...sectionTitle,
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  emptyDescription: {
    whiteSpace: 'pre-wrap',
    mb: 4,
    ...regularMd,
    '@media screen and (max-width: 600px)': {
      marginBottom: '20px',
    },
  },
  uploadButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadFileChip: {
    margin: '0 8px 8px 0',
  },
  uploadSubmitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  // ヘッダーのタイトル横に出す合計レコード数（#3308）。
  // タイトルの補足なので、主張しすぎないサブテキスト色にする。
  recordCountText: {
    ...regularSm,
    color: gray80,
    marginLeft: '16px',
    marginRight: 'auto',
    whiteSpace: 'nowrap',
  },
  // アップロード後にやること（照合設定）の案内（#3180）
  uploadNextStepBox: {
    backgroundColor: yellow_overlay10,
    border: `1px solid ${yellow50}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
    padding: '12px',
  },
  // 次にやることの案内。11px・gray80 では見落とされていたため、
  // 注意を引く配色と本文サイズに上げる（#3308）。
  uploadNextStepText: {
    ...regularSm,
    color: gray100,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  uploadSubmitButtonText: {
    ...boldMd,
    color: white,
  },
  FileNameEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '248px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  CreatedByEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '96px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  // 「マスタ照合」列に出す読取パターン名（#3308）。長いときは省略する。
  patternNamesText: {
    display: 'inline-block',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  batchStatusProcessing: {
    ...regularXs,
    backgroundColor: yellow_overlay10,
    color: yellow50,
    borderRadius: '4px',
    padding: '2px 8px',
    fontWeight: 500,
  },
  batchStatusError: {
    ...regularXs,
    backgroundColor: red20,
    color: red50,
    borderRadius: '4px',
    padding: '2px 8px',
    fontWeight: 500,
  },
};

interface MappingsState {
  [masterId: string]: MasterMappingType[];
}

/**
 * マスタに紐づく読取パターン名を一覧表示用の文字列にする（#3308）。
 *
 * 同じパターンに複数のマスタ照合があっても、パターン名は1つにまとめる
 * （列に出したいのは「どのパターンで使われているか」であって設定の件数ではないため）。
 */
const formatPatternNames = (mappings: MasterMappingType[]): string =>
  Array.from(new Set(mappings.map(mapping => mapping.pattern_name || '未設定パターン'))).join('、');

/** 画面切り替えタブの表示順（#3180）。既定はマスタ一覧。 */
const MASTER_TAB_ORDER: MasterPageTab[] = [masterPageTab.masterList, masterPageTab.patternMapping];

interface SortableMasterRowProps {
  masterId: string;
  /** 並べ替えできるか。閲覧のみのユーザーにはハンドルを出さない（#3308） */
  canReorder: boolean;
  /** マスタ本体の行のセル（先頭のドラッグハンドル列を除く） */
  masterRowCells: React.ReactNode;
}

/**
 * マスタ1件ぶんの行に D&D 並べ替え（#3135）の機能を付与するラッパー。
 *
 * 先頭のドラッグハンドル列は本コンポーネントが描画し、残りの列は
 * masterRowCells で受け取る。
 */
const SortableMasterRow = ({
  masterId,
  canReorder,
  masterRowCells,
}: SortableMasterRowProps): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: masterId,
  });

  return (
    <TableBody
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableRow sx={styles.tableDataRow}>
        {/* 1列目は並べ替えハンドル。
            「パターンごとの照合設定」タブとは列の意味を揃えず、幅だけ合わせている。
            アップロード権限が無いユーザーには出さない（#3308）。ハンドルに listeners が
            付かなくなるため、ポインタ・キーボードどちらからも並べ替えできなくなる。 */}
        <TableCell sx={{ ...styles.tableDataText, width: '32px', padding: '0 4px' }}>
          {canReorder && (
            <IconButton {...attributes} {...listeners} size="small" sx={{ cursor: 'grab' }}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
        {masterRowCells}
      </TableRow>
    </TableBody>
  );
};

// マスタCSVのアップロードで出たエラーを、利用者向けの文言にする（#3416）。
// サーバがアップロードを止める理由（取り込み処理中・レコード数上限）は failed-precondition に
// 日本語のメッセージを載せて返している。getUserErrorMessage は code を優先して定型文にするため、
// failed-precondition のときだけサーバのメッセージをそのまま出す。
const getMasterUploadErrorMessage = (error: unknown): string =>
  error instanceof FirebaseError && error.code === 'functions/failed-precondition'
    ? error.message
    : getUserErrorMessage(error, '予期せぬエラーが発生しました。');

const Master = (): ReactElement => {
  // E-5: 初期表示で「0件→loading→リスト」のちらつきを防ぐため、初期値を true にする。
  // companyId 確定後に fetchMasters が走り、完了で false になる。
  const [loading, setLoading] = useState<boolean>(true);
  // 成功通知（infoText）も出せるよう共通フックに寄せる（#3308）。
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  // ダウンロード中のマスタID（#3308）。行ごとにボタンがあるため boolean では足りない。
  const [downloadingMasterId, setDownloadingMasterId] = useState<string>('');
  // 「最新の状態に更新」の実行中フラグ（#3416）
  const [reloadingStatus, setReloadingStatus] = useState<boolean>(false);
  const [newUploadModalOpen, setNewUploadModalOpen] = useState<boolean>(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState<boolean>(false);
  const [mappingModalOpen, setMappingModalOpen] = useState<boolean>(false);
  const [editingMapping, setEditingMapping] = useState<MasterMappingType | null>(null);
  const [mappingTargetMaster, setMappingTargetMaster] = useState<MasterType | null>(null);
  const [deleteMappingTarget, setDeleteMappingTarget] = useState<{
    master: MasterType;
    mapping: MasterMappingType;
  } | null>(null);
  // マスタ削除の確認モーダル対象（結果削除などと同じ DeleteModal を使う）
  const [deleteMasterTarget, setDeleteMasterTarget] = useState<MasterType | null>(null);
  const [replaceTargetMaster, setReplaceTargetMaster] = useState<MasterType | null>(null);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading } = useContext(AuthContext);
  const { isProxyLogin } = useContext(ProxyLoginContext);
  const { companyId, operatorId } = useCompanyId();

  // アップロード系操作の可否
  //   - 運営の代理ログイン中: 常に許可（運営はいつでもアップロード可能）
  //   - それ以外: 企業の master_self_upload フラグに従う
  const canUpload = isProxyLogin || (company?.master_self_upload ?? false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [masters, setMasters] = useState<MasterType[]>([]);
  const [mappingsByMaster, setMappingsByMaster] = useState<MappingsState>({});
  const [fileError, setFileError] = useState<string | null>(null);
  const { patterns } = usePatternList();

  // 一覧の表示順（#3135）。同じパターンに紐づくマスタが隣接するよう
  // 「代表パターンの作成日 降順 → マスタの作成日 昇順」で並べ替える。
  // マッピングの追加・編集・削除でも並び順が追従するよう mappingsByMaster を依存に含める。
  const patternCreatedAtMap = useMemo(() => buildPatternCreatedAtMap(patterns), [patterns]);
  const sortedMasters = useMemo(
    () => sortMastersByPattern(masters, mappingsByMaster, patternCreatedAtMap),
    [masters, mappingsByMaster, patternCreatedAtMap],
  );

  // ---- タブ構成（#3180）----
  // タブ状態は URL クエリで持ち、リロード・ブラウザバックで維持する。
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get('tab') === masterPageTab.patternMapping
      ? masterPageTab.patternMapping
      : masterPageTab.masterList;
  // パターン別設定タブで並べ替えが未保存のパターンID集合。タブ切替時の離脱確認に使う。
  const [dirtyPatternIds, setDirtyPatternIds] = useState<Set<string>>(new Set());
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  // ステップ追加時のマスタ選択ダイアログ
  const [stepMasterSelectOpen, setStepMasterSelectOpen] = useState<boolean>(false);
  const [stepMasterId, setStepMasterId] = useState<string>('');
  // ステップを追加する対象のパターン（マスタ選択後にモーダルへ引き継ぐ）。
  // パターン行の追加ボタンから開いた場合はそのパターンID、
  // ヘッダーの「パターンにマスタ照合を設定する」から開いた場合は空（ダイアログで選ばせる）。
  const [stepTargetPatternId, setStepTargetPatternId] = useState<string>('');
  // 選択ダイアログでパターンを選ぶときの一時値
  const [stepPatternId, setStepPatternId] = useState<string>('');

  const handleOrderDirtyChange = useCallback((patternId: string, dirty: boolean): void => {
    setDirtyPatternIds(prev => {
      if (dirty === prev.has(patternId)) return prev;
      const next = new Set(prev);
      if (dirty) {
        next.add(patternId);
      } else {
        next.delete(patternId);
      }
      return next;
    });
  }, []);

  const changeTab = (tab: string): void => {
    const action = (): void => {
      const next = new URLSearchParams(searchParams);
      if (tab === masterPageTab.masterList) {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      setSearchParams(next, { replace: true });
    };

    // 未保存の並べ替えがある状態でタブを離れると変更が失われるため確認を挟む（#3180）。
    if (dirtyPatternIds.size > 0) {
      setPendingNavigation(() => action);
      return;
    }
    action();
  };

  // パターン別設定タブに表示する、パターンごとの照合ステップ一覧（#3180）。
  // マスタ照合が1件以上あるパターンのみを対象にする（設定のないパターンは並べても意味がないため）。
  // 各パターン内は execution_order 昇順 → 未設定は後ろ（created_at 昇順）で並べる。
  // 論理削除済みのマスタ・マッピングは fetchMappingsForMaster の時点で除外済み。
  const patternGroups: PatternMappingGroup[] = useMemo(() => {
    const stepsByPatternId = new Map<string, MappingStep[]>();
    for (const master of masters) {
      for (const mapping of mappingsByMaster[master.master_id] ?? []) {
        if (!mapping.pattern_id) continue;
        const steps = stepsByPatternId.get(mapping.pattern_id) ?? [];
        steps.push({ mapping, master });
        stepsByPatternId.set(mapping.pattern_id, steps);
      }
    }

    const toMillis = (mapping: MasterMappingType): number =>
      mapping.created_at ? mapping.created_at.toMillis() : 0;

    // パターン一覧の並び順に合わせる（パターン管理画面と見え方を揃えるため）。
    return patterns
      .filter(pattern => stepsByPatternId.has(pattern.patternId))
      .map(pattern => ({
        patternId: pattern.patternId,
        patternName: pattern.pattern_name,
        steps: (stepsByPatternId.get(pattern.patternId) ?? []).sort((a, b) => {
          const orderA = a.mapping.execution_order;
          const orderB = b.mapping.execution_order;
          // execution_order を持つものを先に、持たないものを後ろに置く（#3180）。
          if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
          if (orderA !== undefined) return -1;
          if (orderB !== undefined) return 1;
          return toMillis(a.mapping) - toMillis(b.mapping);
        }),
      }));
  }, [masters, mappingsByMaster, patterns]);

  // D&D センサー（読取パターン一覧と同じ構成）
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // D&D 並べ替えハンドラー。
  // 楽観的に display_order を書き込んでから永続化し、失敗時は元の値に戻す。
  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedMasters.findIndex(m => m.master_id === active.id);
    const newIndex = sortedMasters.findIndex(m => m.master_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(sortedMasters, oldIndex, newIndex);
    const previousMasters = masters;
    // 並べ替え後の位置をそのまま display_order にして、再ソートしても順序が保たれるようにする
    setMasters(newOrder.map((m, index) => ({ ...m, display_order: index })));

    try {
      await updateMastersDisplayOrder(newOrder.map(m => m.master_id));
    } catch {
      setMasters(previousMasters);
      setErrorText('並び替えの保存に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  // 新規アップロードモーダルの状態（#3180）。
  // 登録するのは CSV と AI照合に使うカラムだけ。パターン紐付けは「パターン別設定」タブで行う。
  const [newCsvColumns, setNewCsvColumns] = useState<string[]>([]);
  const [newSearchCsvColumn, setNewSearchCsvColumn] = useState<string>('');

  useEffect(() => {
    if (isLoading) return;
    // 未認証時の退避は useAuthRedirect が行う（#3203）。ここは企業設定による振り分けのみ扱う
    if (user === null) return;
    if (company !== null && !company.enable_master) navigate('/home');
  }, [user, company, isLoading, navigate]);

  const formatFileSize = (size: number): string => {
    if (size >= 1_000_000_000) return `${(size / 1_000_000_000).toFixed(2)} GB`;
    if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(2)} MB`;
    if (size >= 1_000) return `${(size / 1_000).toFixed(2)} KB`;
    return `${size} B`;
  };

  const fetchMappingsForMaster = async (masterId: string): Promise<MasterMappingType[]> => {
    const list = await getMasterMappings(masterId);
    setMappingsByMaster(prev => ({ ...prev, [masterId]: list }));
    return list;
  };

  const fetchMasters = async (): Promise<void> => {
    if (!companyId) return;
    try {
      setLoading(true);
      const result = await getMastersByCompanyId(companyId);
      setMasters(result);
      await Promise.all(result.map(m => fetchMappingsForMaster(m.master_id)));
      // 展開は既定で閉じておく（#3180）。マスタ一覧は CSV の管理が主目的で、
      // 照合設定は「このマスタ何が紐づいていたっけ？」と確認したいときだけ開けば足りるため。
    } catch {
      setErrorText('マスタデータの取得に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMasters();
  }, [companyId]);

  // 画面マウント時に Cloud SQL csv_job_histories の最新ステータスを Firestore へ同期する。
  // onSnapshot で更新後の値を受け取り画面に反映する。エラー時はサイレント（既存値を表示し続ける）。
  useEffect(() => {
    if (!companyId) return;
    syncMasterBatchStatus({ gendrive_company_id: companyId }).catch(() => {
      // 同期失敗時はフォアグラウンドで通知しない（Firestore の既存値を表示し続ける）
    });
  }, [companyId]);

  // 取り込み状態を手動で取り直す（#3416）。
  // batch_status は画面を開いたときにしか同期しないため、取り込みが終わっても
  // 「処理中」のまま CSV更新ができない。自動で取り直すと仕組みが複雑になるので、
  // 利用者が押して確かめる形にする。
  const reloadMasterStatus = async (): Promise<void> => {
    if (!companyId) return;
    setReloadingStatus(true);
    try {
      await syncMasterBatchStatus({ gendrive_company_id: companyId });
      await fetchMasters();
    } catch (error: unknown) {
      setErrorText(getUserErrorMessage(error, '最新の状態の取得に失敗しました。'));
      setSnackbarOpen(true);
    } finally {
      setReloadingStatus(false);
    }
  };

  // batch_statusのリアルタイム監視（mastersの他フィールドは上書きしない）
  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, 'masters'),
      where('company_id', '==', companyId),
      orderBy('updated_at', 'desc'),
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setMasters(prev => {
        const statusMap = new Map(
          snapshot.docs.map(d => [d.id, d.data().batch_status as MasterType['batch_status']]),
        );
        return prev.map(m => ({ ...m, batch_status: statusMap.get(m.master_id) }));
      });
    });
    return unsubscribe;
  }, [companyId]);

  const downloadCsvFile = async (
    filePath: string,
    fileName: string,
    masterId: string,
  ): Promise<void> => {
    if (!companyId) {
      setErrorText('会社情報が取得できません。');
      setSnackbarOpen(true);
      return;
    }
    // 署名付きURL発行とfetchで待ち時間があるため、完了までボタンを操作不可にする（#3308）。
    setDownloadingMasterId(masterId);
    try {
      // E-6: master-verification バケットは Firebase 管理外のため getDownloadURL では 403 になる。
      // アップロードと同じく署名付きURL（read）を Functions から発行して取得する。
      const { signed_url } = await getMasterCsvSignedDownloadUrl({
        gendrive_company_id: companyId,
        file_path: filePath,
      });
      // 別オリジンのURLは download 属性が効かないため、blob に変換してから保存する。
      const res = await fetch(signed_url);
      if (!res.ok) throw new Error(`download failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setErrorText('ファイルのダウンロードに失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setDownloadingMasterId('');
    }
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<InputUploadType>({
    resolver: yupResolver(inputCsvUploadSchema),
  });

  const handleReplaceFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[] | null) => void,
  ): Promise<void> => {
    setFileError(null);
    if (files && files.length > 0) {
      const selectedFile = files[0];
      // E-2/E-3: 新規アップロードと同じく拡張子・MIME・サイズ・UTF-8 を検証する。
      const validationError = await validateMasterCsvFile(selectedFile);
      if (validationError) {
        setFileError(validationError);
        onChange([selectedFile]);
        return;
      }
      // 同じファイル名のみ許可（embedding-api 側で差分処理するため）
      if (selectedFile.name !== replaceTargetMaster?.file_name) {
        setFileError('選択したファイルの名前が一致しません。');
        onChange([selectedFile]);
        return;
      }
      // レコード数上限のチェック（#3288）。選択した時点で気付けるよう、
      // 更新処理を待たずにここで弾く。
      const limitError = await validateMasterRecordLimit(
        selectedFile,
        replaceTargetMaster?.master_id,
      );
      if (limitError) {
        setFileError(limitError);
        onChange([selectedFile]);
        return;
      }
      onChange([selectedFile]);
    } else {
      onChange(null);
    }
  };

  const handleFilesDeleted = (): void => {
    setValue('images', []);
    trigger('images');
    setFileError(null);
  };

  // レコード数上限を超えないか検証する（#3288）。
  // 超過していればエラーメッセージ、問題なければ null を返す。
  // excludeMasterId は CSV更新時の差し替え対象（既存行数が入れ替わって消えるため合計から除く）。
  const validateMasterRecordLimit = async (
    file: File,
    excludeMasterId?: string,
  ): Promise<string | null> => {
    const rowCount = await getCsvRowCount(file);
    const limitCheck = checkMasterRecordLimit({
      masters,
      uploadingRowCount: rowCount,
      limit: company?.master_record_limit,
      excludeMasterId,
    });

    if (!limitCheck.exceeded) return null;
    // exceeded=true は上限が設定されている場合のみ成立する
    return buildMasterRecordLimitErrorMessage(
      limitCheck.totalRowCount,
      company?.master_record_limit ?? 0,
    );
  };

  // ヘッダー行（1行目）だけを得るために先頭チャンクのみ読む。全体を読み込まない。
  const parseCsvHeaders = async (file: File): Promise<string[]> => {
    const headerChunkSize = 64 * 1024; // 64KB（ヘッダー1行には十分）
    const text = await file.slice(0, headerChunkSize).text();
    const firstLine = text.split('\n')[0];
    if (!firstLine) return [];
    return firstLine.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
  };

  // rowCount / replaceMasterId はサーバ側のレコード数上限判定に渡す（#3336）。
  // 上限超過の場合は署名付きURLが発行されず、ここで例外になる。
  const uploadCsvFile = async (
    file: File,
    masterDatasetId: number,
    rowCount: number,
    replaceMasterId?: string,
  ): Promise<string> => {
    if (!companyId) throw new Error('会社情報が取得できません');

    const contentType = file.type || 'text/csv';
    const { signed_url, file_path } = await getMasterCsvSignedUploadUrl({
      gendrive_company_id: companyId,
      master_dataset_id: masterDatasetId,
      file_name: file.name,
      content_type: contentType,
      row_count: rowCount,
      replace_master_id: replaceMasterId,
    });

    const response = await fetch(signed_url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`CSVのアップロードに失敗しました。(${response.status})`);
    }

    return file_path;
  };

  // 新規マスタアップロード（#3180）。
  // ここで登録するのは CSV と embedding 対象列（AI照合に使うカラム）だけ。
  // どの読取パターンとどう照合するかは「パターン別設定」タブで後から設定する。
  // マスタとパターンは多対多で、登録時点では紐付け先が決まっていないことが多いため。
  const onNewUploadSubmit = async (file: File): Promise<void> => {
    setLoading(true);
    try {
      if (!user) throw new Error('ログインしてください。');
      if (!companyId) throw new Error('会社情報が取得できません。');

      const csvColumns = newCsvColumns.length > 0 ? newCsvColumns : await parseCsvHeaders(file);

      const rowCount = await getCsvRowCount(file);

      // レコード数上限の最終ガード（#3288）。ファイル選択時にも検証しているが、
      // 選択後に他タブでマスタが追加された場合に備える。Cloud SQL への登録より前に行う
      // （登録後に弾くと Firestore に載らないまま master_datasets だけが残る「孤児」になる）。
      const limitError = await validateMasterRecordLimit(file);
      if (limitError) {
        setFileError(limitError);
        return;
      }

      // 1. Cloud SQL に master_datasets を登録して master_dataset_id を取得。
      // パターン紐付け（master_field_mappings）は照合設定時に行うため、ここでは渡さない。
      const { master_dataset_id } = await registerMasterDataset({
        gendrive_company_id: companyId,
        file_name: file.name,
        search_key: newSearchCsvColumn,
      });

      // 2. GCS に正しいパス（{companyId}/{master_dataset_id}/{fileName}）でアップロード
      const filePath = await uploadCsvFile(file, master_dataset_id, rowCount);

      // 3. Firestore に masters ドキュメントを登録
      await createMaster({
        companyId,
        fileName: file.name,
        filePath,
        fileSize: formatFileSize(file.size),
        contentType: file.type || 'text/csv',
        rowCount,
        csvColumns,
        // AI照合の照合先列（= DB の search_key）。マッピング編集で書き換えない不変値（#3082）。
        searchKey: newSearchCsvColumn,
        // 代理ログイン中は「事務局」名義で保存する（#3343）
        ...buildUploaderFields(user, isProxyLogin, operatorId),
      });

      setNewUploadModalOpen(false);
      reset({ images: [] });
      resetNewMappingForm();
      await fetchMasters();
    } catch (error: unknown) {
      setErrorText(getMasterUploadErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // CSV更新（同じパスに上書きアップロード → embedding-api 側で差分処理）
  const onReplaceSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      if (!user) throw new Error('ログインしてください。');
      if (!replaceTargetMaster) return;

      const file = watch('images')?.[0];
      if (!file) return;
      // 拡張子は小文字の .csv のみ許可（選択時にも validateMasterCsvFile で弾いているが最終ガード）（#3050）。
      if (!/\.csv$/.test(file.name)) {
        setFileError('CSV ファイル (.csv) を選択してください。');
        setLoading(false);
        return;
      }
      if (file.name !== replaceTargetMaster.file_name) {
        setFileError('選択したファイルの名前が一致しません。');
        setLoading(false);
        return;
      }

      // ヘッダー列が元のマスタと一致するか検証
      // 既存マッピング設定 (search_csv_column / column_mappings) が壊れるのを防ぐため。
      const csvColumns = await parseCsvHeaders(file);
      const originalColumns = replaceTargetMaster.csv_columns ?? [];
      const sameColumns =
        csvColumns.length === originalColumns.length &&
        csvColumns.every((col, i) => col === originalColumns[i]);
      if (!sameColumns) {
        setFileError('CSV のヘッダー列が元のマスタと一致しません。');
        setLoading(false);
        return;
      }

      const rowCount = await getCsvRowCount(file);
      if (rowCount <= 0) {
        setFileError('データ行が存在しません（ヘッダーのみの CSV は登録できません）。');
        setLoading(false);
        return;
      }

      // レコード数上限の最終ガード（#3288）。ファイル選択時にも検証しているが、
      // 選択後に他タブでマスタが追加された場合に備える。
      const limitError = await validateMasterRecordLimit(file, replaceTargetMaster.master_id);
      if (limitError) {
        setFileError(limitError);
        setLoading(false);
        return;
      }

      // 既存の file_path から master_dataset_id を取り出して同じパスに上書きアップロード
      // file_path: {companyId}/{masterDatasetId}/{fileName}
      const pathParts = replaceTargetMaster.file_path.split('/');
      const masterDatasetId = Number(pathParts[1]);
      await uploadCsvFile(file, masterDatasetId, rowCount, replaceTargetMaster.master_id);

      await updateMaster({
        masterId: replaceTargetMaster.master_id,
        fileSize: formatFileSize(file.size),
        rowCount,
        csvColumns,
        // 代理ログイン中は「事務局」名義で保存する（#3343）
        ...buildUploaderFields(user, isProxyLogin, operatorId),
      });

      setReplaceModalOpen(false);
      setReplaceTargetMaster(null);
      reset({ images: [] });
      await fetchMasters();
    } catch (error: unknown) {
      setErrorText(getMasterUploadErrorMessage(error));
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMaster = async (): Promise<void> => {
    const master = deleteMasterTarget;
    if (!master) return;
    try {
      setLoading(true);
      // 論理削除（#2896）。物理削除はせず status=99 にする。運営が後から
      // マスタ・マッピングの使われ方を確認できるようデータは残す。
      // 影響 pattern の has_master_mapping 再評価に使うため、削除前にマッピングを取得しておく。
      const mappings = await getMasterMappings(master.master_id);

      // Firestore の master とサブコレクション master_mappings を status=99 にする
      await deleteMaster(master.master_id);

      // Cloud SQL（master_field_mappings / master_records）は残す。
      // 照合・表示は Firestore の status=99 で除外されるため、ここでは物理削除しない。

      // 影響を受けた pattern ごとに has_master_mapping を再評価（重複は除く）。
      // 再評価は論理削除済み（status=99）を除いた件数で行う（課金・照合UIを止める）。
      const affectedPatternIds = Array.from(
        new Set(mappings.map(m => m.pattern_id).filter((id): id is string => Boolean(id))),
      );
      await Promise.all(affectedPatternIds.map(pid => updatePatternMasterMappingFlag(pid)));

      await fetchMasters();
    } catch {
      setErrorText('マスタの削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setDeleteMasterTarget(null);
    }
  };

  /**
   * pattern.has_master_mapping を、そのパターンに紐づく master_mappings の有無に応じて更新する。
   * - hasMapping=true 強制 / false で判定 / undefined なら自動判定（残数を数える）
   * 判定失敗してもメイン処理を止めない（料金加算側のフラグなので致命的でないため、フォールバックでログ警告のみ）。
   */
  const updatePatternMasterMappingFlag = async (
    patternId: string,
    hasMapping?: boolean,
  ): Promise<void> => {
    if (!patternId) return;
    try {
      let nextValue: boolean;
      if (hasMapping !== undefined) {
        nextValue = hasMapping;
      } else {
        // master_mappings 全社横断のうち、当該 pattern_id を持つ「有効な」ドキュメントが1件以上あるか。
        // 論理削除済み（status=99）の master / mapping は数えない（#2896）。
        const snap = await getDocs(
          query(collection(db, 'masters'), where('company_id', '==', companyId)),
        );
        let total = 0;
        for (const masterDoc of snap.docs) {
          // 親 master が論理削除済みなら配下マッピングは数えない
          if ((masterDoc.data()?.status as number | undefined) === masterStatus.deleted) continue;
          // pattern_id での where はサブコレクションの単一フィールドインデックスを要求するため、
          // 全件取得してコード側で pattern_id と status を判定する（#2905）。
          const subSnap = await getDocs(collection(masterDoc.ref, 'master_mappings'));
          const activeCount = subSnap.docs.filter(d => {
            const data = d.data() as { pattern_id?: string; status?: number };
            return data.pattern_id === patternId && data.status !== masterStatus.deleted;
          }).length;
          total += activeCount;
          if (total > 0) break;
        }
        nextValue = total > 0;
      }
      await updateDoc(doc(db, 'patterns', patternId), {
        has_master_mapping: nextValue,
      });
    } catch {
      // フラグ更新失敗はメイン処理を止めない（コスト計算の判定用フラグなので致命的ではない）。
      // 次回のマッピング操作時に再判定されるため、サイレントに無視する。
    }
  };

  const handleDeleteMapping = async (): Promise<void> => {
    if (!deleteMappingTarget) return;
    const { master, mapping } = deleteMappingTarget;
    try {
      setLoading(true);
      // Firestore削除
      await deleteMasterMapping(master.master_id, mapping.mapping_id);
      // Cloud SQL の master_field_mappings も削除。
      // 登録時と同じく検索キーのラベルが source_field_name のため、正規化して取る（#3180）。
      const deletedSearchItemLabel = resolveSearchKey(mapping).item_label;
      if (companyId && mapping.pattern_id && deletedSearchItemLabel) {
        await deleteMasterFieldMapping({
          gendrive_company_id: companyId,
          pattern_id: mapping.pattern_id,
          source_field_name: deletedSearchItemLabel,
        });
      }
      // 削除後にこのパターンの残マッピング数を再カウントしてフラグを再評価
      if (mapping.pattern_id) {
        await updatePatternMasterMappingFlag(mapping.pattern_id);
      }
      await fetchMappingsForMaster(master.master_id);
      setDeleteMappingTarget(null);
    } catch {
      setErrorText('マスタ照合の削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const openMappingModal = (master: MasterType, mapping: MasterMappingType | null): void => {
    setMappingTargetMaster(master);
    setEditingMapping(mapping);
    setMappingModalOpen(true);
  };

  /**
   * パターン別設定タブの並べ替えを確定保存する（#3180）。
   * 表示順をそのまま execution_order（0 始まりの連番）にする。
   */
  const handleSaveStepOrder = async (
    _patternId: string,
    orderedSteps: MappingStep[],
  ): Promise<void> => {
    try {
      await updateMasterMappingOrders(
        orderedSteps.map((step, index) => ({
          masterId: step.master.master_id,
          mappingId: step.mapping.mapping_id,
          executionOrder: index,
        })),
      );
      // 保存済みの順序を画面へ反映するため、関係するマスタのマッピングを取り直す。
      const affectedMasterIds = Array.from(
        new Set(orderedSteps.map(step => step.master.master_id)),
      );
      await Promise.all(affectedMasterIds.map(masterId => fetchMappingsForMaster(masterId)));
      setInfoText('照合順を保存しました。');
      setSnackbarOpen(true);
    } catch (error) {
      setErrorText('照合順の保存に失敗しました。');
      setSnackbarOpen(true);
      // 握りつぶすと呼び出し元が失敗を検知できず、未保存の並びを保存済みとして
      // 扱ってしまう（＝並びが黙って失われる）。呼び出し元へ伝播させる（#3308）。
      throw error;
    }
  };

  const handleMappingSubmit = async (data: MasterMappingFormResult): Promise<void> => {
    if (!mappingTargetMaster) return;
    try {
      // Cloud SQL の master_field_mappings に照合キーを登録
      // file_path: {companyId}/{masterDatasetId}/{fileName} から master_dataset_id を取得
      // 絞り込みキー（#3180）は raw_data の列へ直接あたるため、ここには登録しない。
      if (companyId) {
        const pathParts = mappingTargetMaster.file_path.split('/');
        const existingMasterDatasetId = Number(pathParts[1]) || undefined;

        // 編集で照合元項目（= source_field_name）を別項目に変えた場合（#2905）。
        // Cloud SQL のキーは (company_id, pattern_id, source_field_name) なので、
        // 先に旧名→新名へ UPDATE しておかないと、後続の registerMasterDataset(upsert) が
        // 新名で別行を INSERT し、旧名の行が孤児として残ってしまう。
        // 同一パターン内で label が変わったときのみ rename する。
        // 旧データは search_key を持たないため、比較元は resolveSearchKey で正規化して取る。
        const previousSearchItemLabel = editingMapping
          ? resolveSearchKey(editingMapping).item_label
          : '';
        if (
          editingMapping &&
          editingMapping.pattern_id === data.patternId &&
          previousSearchItemLabel &&
          previousSearchItemLabel !== data.searchKey.item_label
        ) {
          await renameMasterFieldMapping({
            gendrive_company_id: companyId,
            pattern_id: data.patternId,
            renames: [
              {
                old_source_field_name: previousSearchItemLabel,
                new_source_field_name: data.searchKey.item_label,
              },
            ],
          });
        }

        await registerMasterDataset({
          gendrive_company_id: companyId,
          file_name: mappingTargetMaster.file_name,
          search_key: data.searchKey.csv_column,
          source_field_name: data.searchKey.item_label,
          pattern_id: data.patternId,
          existing_master_dataset_id: existingMasterDatasetId,
        });
      }

      if (editingMapping) {
        await updateMasterMapping({
          masterId: mappingTargetMaster.master_id,
          mappingId: editingMapping.mapping_id,
          patternId: data.patternId,
          patternName: data.patternName,
          searchKey: data.searchKey,
          filterKeys: data.filterKeys,
          autoApplyScore: data.autoApplyScore,
          reviewThreshold: data.reviewThreshold,
          enableLlmRerank: data.enableLlmRerank,
          columnMappings: data.columnMappings,
          scopeItemId: data.scopeItemId,
          scopeItemLabel: data.scopeItemLabel,
        });
      } else {
        await createMasterMapping({
          masterId: mappingTargetMaster.master_id,
          patternId: data.patternId,
          patternName: data.patternName,
          searchKey: data.searchKey,
          filterKeys: data.filterKeys,
          autoApplyScore: data.autoApplyScore,
          reviewThreshold: data.reviewThreshold,
          enableLlmRerank: data.enableLlmRerank,
          columnMappings: data.columnMappings,
          scopeItemId: data.scopeItemId,
          scopeItemLabel: data.scopeItemLabel,
        });
      }
      // マッピングが 1 件以上ある状態なので true 固定で更新（再カウント不要）
      await updatePatternMasterMappingFlag(data.patternId, true);
      // 編集時にパターン ID が変わったケースに備え、旧パターン側も再判定
      if (
        editingMapping &&
        editingMapping.pattern_id &&
        editingMapping.pattern_id !== data.patternId
      ) {
        await updatePatternMasterMappingFlag(editingMapping.pattern_id);
      }
      await fetchMappingsForMaster(mappingTargetMaster.master_id);
      setMappingModalOpen(false);
      setMappingTargetMaster(null);
      setEditingMapping(null);
      setStepTargetPatternId('');
    } catch (error) {
      // 失敗の原因が分からないと切り分けができないため握りつぶさない（#3309）。
      // この保存は renameMasterFieldMapping / registerMasterDataset /
      // updateMasterMapping の3つを呼ぶので、どれで落ちたかが重要になる。
      Sentry.captureException(error);
      // 前置きがあるので、原因部分だけを続けて出す（定型の「エラーが発生しました。」は使わない）
      const message = getUserErrorMessage(error, '');
      setErrorText(`マスタ照合の保存に失敗しました。${message ? `（${message}）` : ''}`);
      setSnackbarOpen(true);
    }
  };

  const openReplaceModal = (master: MasterType): void => {
    setReplaceTargetMaster(master);
    reset({ images: [] });
    setFileError(null);
    setReplaceModalOpen(true);
  };

  const getCsvColumns = (master: MasterType): string[] => {
    return master.csv_columns ?? [];
  };

  const resetNewMappingForm = (): void => {
    setNewCsvColumns([]);
    setNewSearchCsvColumn('');
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { '@media screen and (max-width:960px)': { paddingBottom: '84px' } },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Typography
            sx={{
              ...boldXxl,
              color: gray100,
              '@media screen and (max-width:960px)': { ...spHeaderTitle },
            }}
          >
            マスタ照合管理
          </Typography>
          {/* 登録済みの合計レコード数（#3308）。上限が設定されている企業では上限も添え、
              あとどれだけ登録できるかをアップロード前に把握できるようにする。 */}
          {activeTab === masterPageTab.masterList && masters.length > 0 && (
            <Typography sx={styles.recordCountText}>
              {company?.master_record_limit === undefined || company?.master_record_limit === null
                ? `合計 ${sumMasterRowCount(masters).toLocaleString()} 件`
                : `合計 ${sumMasterRowCount(masters).toLocaleString()} 件 / 上限 ${company.master_record_limit.toLocaleString()} 件`}
            </Typography>
          )}
          {/* ヘッダーのボタンは選択中タブの主操作に合わせる（#3180）。
              マスタ一覧＝CSVを登録する／パターンごとの照合設定＝パターンにマスタ照合を設定する。 */}
          <Box sx={styles.headerButtonWrapper}>
            {canUpload &&
              (activeTab === masterPageTab.patternMapping ? (
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  sx={styles.uploadButton}
                  onClick={(): void => {
                    // パターン未指定で開く＝モーダル側でパターンから選ばせる。
                    setStepTargetPatternId('');
                    setStepMasterSelectOpen(true);
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                    パターンにマスタ照合を設定する
                  </Typography>
                </Button>
              ) : (
                <Button
                  startIcon={<UploadFileIcon />}
                  sx={styles.uploadButton}
                  onClick={(): void => {
                    reset({ images: [] });
                    setFileError(null);
                    resetNewMappingForm();
                    setNewUploadModalOpen(true);
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>マスタ登録</Typography>
                </Button>
              ))}
          </Box>
        </Box>

        {/* 画面切り替えタブ（#3180）。
            OCR結果一覧（未処理/確認済/出力済）と同じチップ型で統一する。 */}
        <Box sx={styles.tabWrapper}>
          {MASTER_TAB_ORDER.map(tab => (
            <Box
              key={tab}
              sx={[styles.tab, activeTab === tab && styles.tabActive]}
              onClick={(): void => changeTab(tab)}
            >
              <Typography sx={[styles.tabText, activeTab === tab && styles.tabTextActive]}>
                {masterPageTabLabel[tab]}
              </Typography>
            </Box>
          ))}
          {/* 取り込み状態は自動では取り直さないため、利用者が押して確かめる（#3416）。
              CSV更新ボタンがあるマスタ一覧タブでだけ出す。 */}
          {activeTab === masterPageTab.masterList && (
            <Button
              onClick={(): void => {
                void reloadMasterStatus();
              }}
              disabled={loading || reloadingStatus}
              startIcon={
                reloadingStatus ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshIcon sx={styles.icon18} />
                )
              }
              sx={[tertiaryButtonBackgroundColor, styles.reloadButton]}
            >
              最新の状態に更新
            </Button>
          )}
        </Box>

        <>
          {activeTab === masterPageTab.patternMapping ? (
            <PatternMappingTab
              groups={patternGroups}
              loading={loading}
              // マスタ照合の編集・削除は、マスタをアップロードできない企業でも
              // 従来どおり可能にする（#2842 の導入時から canUpload の対象外だった）。#3308
              canEditMapping
              // 追加はアップロード権限に連動させる。運営のみアップロード可の企業では、
              // 企業側のユーザーに新規の紐付けを作らせない（#3308）。
              canAddMapping={canUpload}
              canReorder={canUpload}
              onDirtyChange={handleOrderDirtyChange}
              onAddStep={(patternId): void => {
                setStepTargetPatternId(patternId);
                setStepMasterSelectOpen(true);
              }}
              onEditStep={(step): void => openMappingModal(step.master, step.mapping)}
              onDeleteStep={(step): void =>
                setDeleteMappingTarget({ master: step.master, mapping: step.mapping })
              }
              onSaveOrder={handleSaveStepOrder}
            />
          ) : (
            <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
              <Box sx={styles.tableSection}>
                {loading ? (
                  <CustomLoading loading={true} />
                ) : masters.length > 0 ? (
                  <Box sx={styles.tableWrapper}>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={sortedMasters.map(m => m.master_id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Table sx={styles.table}>
                          <TableHead>
                            <TableRow sx={styles.tableHeadRow}>
                              <TableCell
                                sx={{ ...styles.tableHeadText, width: '32px', padding: '0 4px' }}
                              ></TableCell>
                              <TableCell sx={styles.tableHeadText}>ファイル名</TableCell>
                              <TableCell sx={styles.tableHeadText}>設定中のパターン</TableCell>
                              <TableCell sx={styles.tableHeadText}>件数</TableCell>
                              <TableCell sx={styles.tableHeadText}>容量</TableCell>
                              <TableCell sx={styles.tableHeadText}>更新日</TableCell>
                              <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>

                          {/* マスタごとに tbody を分け、それを並べ替えの単位にする（#3308）。
                              こうするとドラッグ時に見出し行・展開中のマッピング行も一緒に動く。 */}
                          {sortedMasters.map(data => {
                            const mappings = mappingsByMaster[data.master_id] ?? [];
                            return (
                              <SortableMasterRow
                                key={data.master_id}
                                masterId={data.master_id}
                                canReorder={canUpload}
                                masterRowCells={
                                  <>
                                    <TableCell sx={styles.tableDataText}>
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                      >
                                        <Tooltip title={data.file_name}>
                                          <span style={styles.FileNameEllipsisText}>
                                            {data.file_name}
                                          </span>
                                        </Tooltip>
                                        {data.batch_status === batchStatus.processing && (
                                          <Box component="span" sx={styles.batchStatusProcessing}>
                                            処理中
                                          </Box>
                                        )}
                                        {data.batch_status === batchStatus.error && (
                                          <Box component="span" sx={styles.batchStatusError}>
                                            失敗
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>
                                    {/* 紐づいている読取パターン名（#3308）。
                                        マスタ一覧はアップロード済みマスタの一覧に徹し、
                                        照合設定の中身は「パターンごとの照合設定」タブで見せる。
                                        同じパターンに複数の照合設定があっても名前は1つにまとめる。 */}
                                    <TableCell sx={styles.tableDataText}>
                                      {mappings.length > 0 ? (
                                        <Tooltip title={formatPatternNames(mappings)}>
                                          <Box component="span" sx={styles.patternNamesText}>
                                            {formatPatternNames(mappings)}
                                          </Box>
                                        </Tooltip>
                                      ) : (
                                        '未設定'
                                      )}
                                    </TableCell>
                                    <TableCell sx={styles.tableDataText}>
                                      {data.row_count + '件'}
                                    </TableCell>
                                    <TableCell sx={styles.tableDataText}>
                                      {data.file_size}
                                    </TableCell>
                                    <TableCell sx={styles.tableDataText}>
                                      {data.updated_at
                                        ? moment(data.updated_at.toDate()).format(
                                            'YYYY/MM/DD HH:mm',
                                          )
                                        : '-'}
                                    </TableCell>
                                    <TableCell sx={styles.tableCellAvatar}>
                                      <Avatar
                                        src={
                                          data.is_proxy
                                            ? HiStoryIcon
                                            : data.uploaded_by_icon || AccountCircle
                                        }
                                        sx={styles.avatar}
                                      />
                                      <Typography>
                                        {/* 代理アップロードは「事務局」と表示する（#3343） */}
                                        <Tooltip
                                          title={getDisplayName(
                                            data.uploaded_by_name,
                                            data.is_proxy,
                                          )}
                                        >
                                          <span style={styles.CreatedByEllipsisText}>
                                            {getDisplayName(data.uploaded_by_name, data.is_proxy)}
                                          </span>
                                        </Tooltip>
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={styles.tableCellButton}>
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          gap: '8px',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {canUpload && (
                                          <>
                                            {/* CSV 更新: 同じファイル名で上書きし、embedding-api 側で
                                        差分処理（INSERT / UPDATE / SKIP）が走る。 */}
                                            <Tooltip
                                              title={
                                                data.batch_status === batchStatus.processing
                                                  ? '取り込み処理中のため更新できません。完了後に「最新の状態に更新」を押してから再度お試しください'
                                                  : '同じファイル名のCSVを再アップロードして差分取り込み（追加/更新/スキップ）を行います'
                                              }
                                            >
                                              {/* disabled のボタンはイベントを受けないため、Tooltip 用に span で包む */}
                                              <span>
                                                <Button
                                                  onClick={(): void => openReplaceModal(data)}
                                                  // 取り込み中の追加アップロードで 2 つの CSV が混ざるのを防ぐ（#3416）
                                                  disabled={
                                                    data.batch_status === batchStatus.processing
                                                  }
                                                  startIcon={<UploadFileIcon sx={styles.icon18} />}
                                                  sx={[
                                                    secondaryButtonBackgroundColor,
                                                    styles.buttonSmall,
                                                  ]}
                                                >
                                                  CSV更新
                                                </Button>
                                              </span>
                                            </Tooltip>
                                          </>
                                        )}
                                        <Button
                                          onClick={(): void => {
                                            void downloadCsvFile(
                                              data.file_path,
                                              data.file_name,
                                              data.master_id,
                                            );
                                          }}
                                          disabled={downloadingMasterId === data.master_id}
                                          startIcon={
                                            downloadingMasterId === data.master_id ? (
                                              <CircularProgress size={16} color="inherit" />
                                            ) : (
                                              <FileDownloadOutlined sx={styles.icon18} />
                                            )
                                          }
                                          sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                                        >
                                          {downloadingMasterId === data.master_id
                                            ? '出力中'
                                            : '出力'}
                                        </Button>
                                        {canUpload && (
                                          <Button
                                            onClick={(): void => {
                                              setDeleteMasterTarget(data);
                                            }}
                                            startIcon={<DeleteOutline sx={styles.icon18} />}
                                            sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                                          >
                                            削除
                                          </Button>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </>
                                }
                              />
                            );
                          })}
                        </Table>
                      </SortableContext>
                    </DndContext>
                  </Box>
                ) : (
                  <>
                    <DescriptionIcon sx={styles.emptyIcon} />
                    <Typography sx={styles.emptyTitle}>マスタデータがありません</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      {/* E-1: アップロード権限の有無で文言を出し分ける。
                      閲覧のみ（運営登録）のユーザーにはアップロードを促さない。 */}
                      <Typography sx={styles.emptyDescription}>
                        {canUpload
                          ? '右上の「マスタ登録」ボタンからCSVファイルをアップロードしてください。'
                          : 'マスタデータは運営側で登録されます。'}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid2>
          )}
        </>
      </Box>

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      {/* 新規CSVアップロード＋1件目マッピング設定ダイアログ */}
      <Dialog open={newUploadModalOpen} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitleText}>マスタデータをアップロードする</Typography>
          <IconButton
            onClick={(): void => {
              setNewUploadModalOpen(false);
              resetNewMappingForm();
            }}
            sx={styles.iconButton}
          >
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>

        <Typography sx={{ ...regularSm, marginBottom: '16px' }}>
          CSVファイルをアップロードします。マスタ照合の設定は「パターンごとの照合設定」タブで行います。
        </Typography>

        {watch('images') && watch('images')[0] && (
          <Box>
            <Chip
              label={watch('images')[0].name}
              onDelete={(): void => {
                handleFilesDeleted();
                resetNewMappingForm();
              }}
              sx={styles.uploadFileChip}
              variant="outlined"
            />
          </Box>
        )}

        <Controller
          control={control}
          name="images"
          render={({ field }): JSX.Element => (
            <FileUploader
              accept=".csv,text/csv"
              helperText={fileError || errors.images?.message}
              multiple={false}
              onFilesSelected={async (files: FileList | null): Promise<void> => {
                setFileError(null);
                if (files && files.length > 0) {
                  const file = files[0];
                  // E-2/E-3: 選択時点で拡張子・MIME・サイズ・UTF-8 を検証し、
                  // 不正なファイル（CSV以外・非UTF-8）はヘッダー解析前に弾く。
                  const validationError = await validateMasterCsvFile(file);
                  if (validationError) {
                    setFileError(validationError);
                    field.onChange([file]);
                    setNewCsvColumns([]);
                    return;
                  }
                  // レコード数上限のチェック（#3288）。選択した時点で気付けるよう、
                  // 登録処理を待たずにここで弾く。
                  const limitError = await validateMasterRecordLimit(file);
                  if (limitError) {
                    setFileError(limitError);
                    field.onChange([file]);
                    setNewCsvColumns([]);
                    return;
                  }
                  field.onChange([file]);
                  const headers = await parseCsvHeaders(file);
                  setNewCsvColumns(headers);
                } else {
                  field.onChange(null);
                  resetNewMappingForm();
                }
              }}
            />
          )}
        />

        <Typography sx={{ ...regularSm, marginTop: '8px', marginBottom: '16px' }}>
          ファイルのサイズは、350MBまでアップロード可能です。
        </Typography>

        {newCsvColumns.length > 0 && (
          <Box sx={{ marginBottom: '16px' }}>
            <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
              AI照合に使うカラム
            </Typography>
            <Typography
              sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '8px' }}
            >
              この列の値をAIが学習し、意味の近さで照合できるようになります（例：商品名・取引先名）
            </Typography>
            <CustomSelectField
              labelText=""
              list={newCsvColumns.map(col => ({ option: col, value: col }))}
              value={newSearchCsvColumn}
              placeholder="選択してください"
              onChange={(e): void => setNewSearchCsvColumn(String(e.target.value))}
              formMb="4px"
              height="40px"
              width="100%"
            />
            <Typography sx={{ fontSize: '11px', color: red50, lineHeight: 1.4 }}>
              ※
              一度アップロードすると変更できません。別の列で照合したい場合は、新しいマスタとして登録してください。
            </Typography>
          </Box>
        )}

        <Box sx={styles.uploadNextStepBox}>
          <Typography sx={styles.uploadNextStepText}>
            アップロード後、「パターンごとの照合設定」タブで、対象パターンにマスタ照合を設定してください。
          </Typography>
        </Box>

        <Box sx={styles.uploadButtonWrapper}>
          <Button
            sx={styles.uploadCancelButton}
            onClick={(): void => {
              setNewUploadModalOpen(false);
              resetNewMappingForm();
            }}
          >
            <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.uploadSubmitButton}
            // 登録に必要なのは CSV と照合キー列のみ（#3180）。
            // パターンとの紐付けは「パターン別設定」タブで行う。
            disabled={!watch('images')?.[0] || !newSearchCsvColumn}
            onClick={async (): Promise<void> => {
              await handleSubmit(async data => onNewUploadSubmit(data.images[0]))();
            }}
          >
            <Typography sx={styles.uploadSubmitButtonText}>アップロード</Typography>
          </Button>
        </Box>
      </Dialog>

      {/* CSV更新ダイアログ（同名 CSV を上書きし、embedding-api 側で差分処理） */}
      <Dialog open={replaceModalOpen} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitleText}>CSVを更新する</Typography>
          <IconButton onClick={(): void => setReplaceModalOpen(false)} sx={styles.iconButton}>
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>

        <Typography sx={{ ...regularSm, marginBottom: '16px' }}>
          {replaceTargetMaster?.file_name} を更新します。
          <br />
          同名・同ヘッダー構成のCSVファイルを選択してください。
          <br />
          差分（追加・更新・スキップ）はサーバー側で自動判定されます。
        </Typography>

        {watch('images') && watch('images')[0] && (
          <Box>
            <Chip
              label={watch('images')[0].name}
              onDelete={(): void => handleFilesDeleted()}
              sx={styles.uploadFileChip}
              variant="outlined"
            />
          </Box>
        )}

        <Controller
          control={control}
          name="images"
          render={({ field }): JSX.Element => (
            <FileUploader
              accept=".csv,text/csv"
              helperText={fileError || errors.images?.message}
              multiple={false}
              onFilesSelected={(files: FileList | null): void => {
                void handleReplaceFilesSelected(files, field.onChange);
              }}
            />
          )}
        />

        <Box sx={styles.uploadButtonWrapper}>
          <Button sx={styles.uploadCancelButton} onClick={(): void => setReplaceModalOpen(false)}>
            <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.uploadSubmitButton}
            disabled={!watch('images')?.[0] || !!fileError}
            onClick={async (): Promise<void> => {
              await onReplaceSubmit();
            }}
          >
            <Typography sx={styles.uploadSubmitButtonText}>保存</Typography>
          </Button>
        </Box>
      </Dialog>

      {/* マッピング設定モーダル */}
      <MasterMappingFormModal
        open={mappingModalOpen}
        companyId={companyId}
        fileName={mappingTargetMaster?.file_name ?? ''}
        csvColumns={mappingTargetMaster ? getCsvColumns(mappingTargetMaster) : []}
        patterns={patterns}
        initial={editingMapping}
        // AI照合の照合先列は、マスタ登録時に確定した search_key（不変の真値）で固定する（#3082）。
        // 以前は「マッピング配列の先頭 [0] の search_csv_column」を流用していたが、
        // 部分一致マッピングを [0] に置いて照合先列を別列（例: JANコード）に変えると、
        // その値が AI照合の照合先まで巻き込んで固定表示してしまう不具合があった。
        // search_key を持たない旧マスタは従来どおり [0] の search_csv_column にフォールバックする。
        lockedSearchCsvColumn={
          mappingTargetMaster
            ? (mappingTargetMaster.search_key ??
              mappingsByMaster[mappingTargetMaster.master_id]?.[0]?.search_csv_column)
            : undefined
        }
        // パターン別設定タブから追加した場合は対象パターンが決まっているため、モーダル側で選ばせない（#3180）。
        fixedPatternId={stepTargetPatternId || undefined}
        onClose={(): void => {
          setMappingModalOpen(false);
          setMappingTargetMaster(null);
          setEditingMapping(null);
          setStepTargetPatternId('');
        }}
        onSubmit={handleMappingSubmit}
      />

      <DeleteModal
        open={!!deleteMappingTarget}
        onClose={(): void => setDeleteMappingTarget(null)}
        onDelete={(): void => {
          void handleDeleteMapping();
        }}
        title="マスタ照合"
        description={`「${deleteMappingTarget?.mapping.pattern_name ?? ''}」のマスタ照合を削除します。削除すると元に戻すことはできません。よろしいですか？`}
        loading={loading}
      />

      <DeleteModal
        open={!!deleteMasterTarget}
        onClose={(): void => setDeleteMasterTarget(null)}
        onDelete={(): void => {
          void handleDeleteMaster();
        }}
        title="マスタ"
        description={`「${deleteMasterTarget?.file_name ?? ''}」を削除します。削除すると元に戻すことはできません。よろしいですか？`}
        loading={loading}
      />

      {/* パターン別設定タブからステップを追加するときのマスタ選択（#3180）。
          どのマスタを照合に使うかを先に決めてから、マッピング設定モーダルへ進む。 */}
      <Dialog open={stepMasterSelectOpen} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitleText}>パターンにマスタ照合を設定する</Typography>
          <IconButton
            onClick={(): void => {
              setStepMasterSelectOpen(false);
              setStepMasterId('');
              setStepPatternId('');
              setStepTargetPatternId('');
            }}
            sx={{ padding: 0 }}
          >
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>
        {/* ヘッダーの「パターンにマスタ照合を設定する」から開いた場合はパターンが未定なので、
            パターンから選ばせる。パターン行の追加ボタンから開いた場合は決まっているので出さない。 */}
        {!stepTargetPatternId && (
          <Box sx={{ marginBottom: '16px' }}>
            <Typography sx={{ ...boldMd, color: gray80, marginBottom: '4px' }}>
              読取パターン
            </Typography>
            <Typography sx={{ ...regularSm, color: gray80, marginBottom: '8px' }}>
              どの読取パターンにマスタ照合を設定するか選択してください
            </Typography>
            <CustomSelectField
              labelText=""
              list={patterns.map(p => ({ option: p.pattern_name, value: p.patternId }))}
              value={stepPatternId}
              placeholder="選択してください"
              onChange={(e): void => {
                const next = String(e.target.value);
                setStepPatternId(next === '__placeholder__' ? '' : next);
              }}
              formMb="0px"
              height="40px"
              width="100%"
            />
          </Box>
        )}

        <Typography sx={{ ...boldMd, color: gray80, marginBottom: '4px' }}>マスタ</Typography>
        <Typography sx={{ ...regularSm, color: gray80, marginBottom: '8px' }}>
          照合に使うマスタを選択してください
        </Typography>
        <CustomSelectField
          labelText=""
          list={sortedMasters.map(m => ({ option: m.file_name, value: m.master_id }))}
          value={stepMasterId}
          placeholder="選択してください"
          onChange={(e): void => {
            const next = String(e.target.value);
            setStepMasterId(next === '__placeholder__' ? '' : next);
          }}
          formMb="0px"
          height="40px"
          width="100%"
        />
        <Box sx={styles.dialogButtonWrapper}>
          <Button
            sx={styles.dialogCancelButton}
            onClick={(): void => {
              setStepMasterSelectOpen(false);
              setStepMasterId('');
              setStepPatternId('');
              setStepTargetPatternId('');
            }}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.dialogSubmitButton}
            disabled={!stepMasterId || !(stepTargetPatternId || stepPatternId)}
            onClick={(): void => {
              const target = masters.find(m => m.master_id === stepMasterId);
              if (!target) return;
              // パターン行から開いた場合は stepTargetPatternId、
              // ヘッダーから開いた場合はこのダイアログで選んだパターンを引き継ぐ。
              setStepTargetPatternId(stepTargetPatternId || stepPatternId);
              setStepMasterSelectOpen(false);
              setStepMasterId('');
              setStepPatternId('');
              openMappingModal(target, null);
            }}
          >
            <Typography sx={{ ...boldMd, color: white }}>次へ</Typography>
          </Button>
        </Box>
      </Dialog>

      {/* 並べ替えの未保存状態での離脱確認（#3180）。
          誤ってタブ・パターンを切り替えて並べ替えが消えるのを防ぐ。 */}
      <Dialog open={!!pendingNavigation} sx={styles.dialog}>
        <Typography sx={{ ...styles.dialogTitleText, marginBottom: '8px' }}>
          並べ替えが保存されていません
        </Typography>
        <Typography sx={{ ...regularSm, color: gray80 }}>
          「実行順序を保存」を押していないため、並べ替えた内容は破棄されます。移動してよろしいですか？
        </Typography>
        <Box sx={styles.dialogButtonWrapper}>
          <Button sx={styles.dialogCancelButton} onClick={(): void => setPendingNavigation(null)}>
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.dialogSubmitButton}
            onClick={(): void => {
              const action = pendingNavigation;
              setPendingNavigation(null);
              setDirtyPatternIds(new Set());
              action?.();
            }}
          >
            <Typography sx={{ ...boldMd, color: white }}>破棄して移動</Typography>
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Master;
